import { getNodeId, getProjectIdFromNodeId, isNamespacedId } from '@/features/projects/utils/projectUtils';
import type { ProjectNode } from '@/shared/types/projectData';

export interface DocsOpenDocumentRequest {
  nodeId: string;
  projectId?: string;
  candidateNodeIds?: string[];
  title?: string;
  type?: ProjectNode['type'];
  parentId?: string;
  nodeSnapshot?: ProjectNode;
  requestedAt: number;
}

const STALE_REQUEST_MS = 30_000;
const PENDING_DOCS_OPEN_REQUEST_STORAGE_KEY = 'pm.pendingDocsOpenRequest';
const DIRECT_DOCS_OPEN_REQUEST_STORAGE_KEY = 'pm.directDocsOpenRequest';
const DOCS_OPEN_REQUEST_QUERY_KEYS = {
  nodeId: 'docsNodeId',
  projectId: 'docsProjectId',
  candidateNodeIds: 'docsCandidateNodeIds',
  title: 'docsTitle',
  type: 'docsType',
  parentId: 'docsParentId',
} as const;

let pendingDocsOpenRequest: DocsOpenDocumentRequest | undefined;
let bootLocationDocsOpenRequest: DocsOpenDocumentRequest | undefined;
let directLocationDocsOpenRequest: DocsOpenDocumentRequest | undefined;

declare global {
  interface Window {
    __pmPendingDocsOpenRequest?: DocsOpenDocumentRequest;
    __pmDirectDocsOpenRequest?: DocsOpenDocumentRequest;
  }
}

const isBrowser = (): boolean => typeof window !== 'undefined';

const readStoredPendingDocsOpenRequest = (): DocsOpenDocumentRequest | undefined => {
  if (!isBrowser()) {
    return undefined;
  }

  try {
    const raw = window.sessionStorage?.getItem(PENDING_DOCS_OPEN_REQUEST_STORAGE_KEY);
    if (!raw) {
      return undefined;
    }
    return JSON.parse(raw) as DocsOpenDocumentRequest;
  } catch {
    return undefined;
  }
};

const writeStoredPendingDocsOpenRequest = (request: DocsOpenDocumentRequest): void => {
  if (!isBrowser()) {
    return;
  }

  try {
    window.sessionStorage?.setItem(PENDING_DOCS_OPEN_REQUEST_STORAGE_KEY, JSON.stringify(request));
  } catch {
    // Module cache still works when storage is unavailable.
  }
};

const clearStoredPendingDocsOpenRequest = (): void => {
  if (!isBrowser()) {
    return;
  }

  try {
    window.sessionStorage?.removeItem(PENDING_DOCS_OPEN_REQUEST_STORAGE_KEY);
  } catch {
    // Ignore storage failures; clearing memory is enough for this chunk.
  }
};

const readStoredDirectDocsOpenRequest = (): DocsOpenDocumentRequest | undefined => {
  if (!isBrowser()) {
    return undefined;
  }

  try {
    const raw = window.sessionStorage?.getItem(DIRECT_DOCS_OPEN_REQUEST_STORAGE_KEY);
    if (!raw) {
      return undefined;
    }
    return JSON.parse(raw) as DocsOpenDocumentRequest;
  } catch {
    return undefined;
  }
};

const writeStoredDirectDocsOpenRequest = (request: DocsOpenDocumentRequest): void => {
  if (!isBrowser()) {
    return;
  }

  try {
    window.sessionStorage?.setItem(DIRECT_DOCS_OPEN_REQUEST_STORAGE_KEY, JSON.stringify(request));
  } catch {
    // Direct URL requests are also held in module state when storage is unavailable.
  }
};

const clearStoredDirectDocsOpenRequest = (): void => {
  if (!isBrowser()) {
    return;
  }

  try {
    window.sessionStorage?.removeItem(DIRECT_DOCS_OPEN_REQUEST_STORAGE_KEY);
  } catch {
    // Ignore storage failures; clearing the URL/module state is still enough.
  }
};

const readWindowDirectDocsOpenRequest = (): DocsOpenDocumentRequest | undefined => (
  isBrowser() ? window.__pmDirectDocsOpenRequest : undefined
);

const clearWindowDirectDocsOpenRequest = (): void => {
  if (!isBrowser()) {
    return;
  }

  delete window.__pmDirectDocsOpenRequest;
};

const readLocationDocsOpenRequest = (): DocsOpenDocumentRequest | undefined => {
  if (!isBrowser()) {
    return undefined;
  }

  try {
    const params = new URLSearchParams(window.location.search);
    const nodeId = params.get(DOCS_OPEN_REQUEST_QUERY_KEYS.nodeId);
    if (!nodeId) {
      return undefined;
    }

    const candidateNodeIds = (params.get(DOCS_OPEN_REQUEST_QUERY_KEYS.candidateNodeIds) || '')
      .split(',')
      .map(value => value.trim())
      .filter(Boolean);
    const type = params.get(DOCS_OPEN_REQUEST_QUERY_KEYS.type) as ProjectNode['type'] | null;

    const request = {
      nodeId,
      projectId: params.get(DOCS_OPEN_REQUEST_QUERY_KEYS.projectId) || undefined,
      candidateNodeIds: candidateNodeIds.length > 0 ? candidateNodeIds : undefined,
      title: params.get(DOCS_OPEN_REQUEST_QUERY_KEYS.title) || undefined,
      type: type || undefined,
      parentId: params.get(DOCS_OPEN_REQUEST_QUERY_KEYS.parentId) || undefined,
      requestedAt: Date.now(),
    };
    directLocationDocsOpenRequest = request;
    if (isBrowser()) {
      window.__pmDirectDocsOpenRequest = request;
    }
    writeStoredDirectDocsOpenRequest(request);
    return request;
  } catch {
    return undefined;
  }
};

bootLocationDocsOpenRequest = readLocationDocsOpenRequest();

const writeLocationDocsOpenRequest = (request: DocsOpenDocumentRequest): void => {
  if (!isBrowser()) {
    return;
  }

  try {
    const url = new URL(window.location.href);
    url.searchParams.set(DOCS_OPEN_REQUEST_QUERY_KEYS.nodeId, request.nodeId);

    const optionalParams: Array<[string, string | undefined]> = [
      [DOCS_OPEN_REQUEST_QUERY_KEYS.projectId, request.projectId],
      [DOCS_OPEN_REQUEST_QUERY_KEYS.candidateNodeIds, request.candidateNodeIds?.join(',')],
      [DOCS_OPEN_REQUEST_QUERY_KEYS.title, request.title],
      [DOCS_OPEN_REQUEST_QUERY_KEYS.type, request.type],
      [DOCS_OPEN_REQUEST_QUERY_KEYS.parentId, request.parentId],
    ];

    optionalParams.forEach(([key, value]) => {
      if (value) {
        url.searchParams.set(key, value);
      } else {
        url.searchParams.delete(key);
      }
    });

    window.history.replaceState({ ...(window.history.state || {}), docsOpenRequest: request.nodeId }, '', url);
  } catch {
    // The in-memory and session-backed queues remain the primary runtime path.
  }
};

const clearLocationDocsOpenRequest = (): void => {
  if (!isBrowser()) {
    return;
  }

  try {
    const url = new URL(window.location.href);
    Object.values(DOCS_OPEN_REQUEST_QUERY_KEYS).forEach((key) => url.searchParams.delete(key));
    window.history.replaceState({ ...(window.history.state || {}), docsOpenRequest: undefined }, '', url);
  } catch {
    // Ignore history failures; selection state is still cleared in memory/storage.
  }
};

const isFreshRequest = (request: DocsOpenDocumentRequest | undefined): request is DocsOpenDocumentRequest => {
  if (!request?.nodeId) {
    return false;
  }
  return Date.now() - request.requestedAt <= STALE_REQUEST_MS;
};

const requestNodeIds = (request: DocsOpenDocumentRequest | undefined): Set<string> => (
  new Set(
    [
      request?.nodeId,
      ...(request?.candidateNodeIds || []),
    ]
    .filter((value): value is string => typeof value === 'string' && value.length > 0)
    .flatMap((value) => [value, getNodeId(value)])
  )
);

const nodeIdMatchesRequest = (
  nodeId: string | undefined,
  request: DocsOpenDocumentRequest | undefined,
): boolean => {
  if (!nodeId || !request?.nodeId) {
    return false;
  }
  const requestedNodeIds = [nodeId, getNodeId(nodeId)];
  const currentNodeIds = requestNodeIds(request);
  return requestedNodeIds.some((value) => currentNodeIds.has(value));
};

export const getPendingDocsOpenRequest = (): DocsOpenDocumentRequest | null => {
  const requestSources = [
    readLocationDocsOpenRequest(),
    directLocationDocsOpenRequest,
    readWindowDirectDocsOpenRequest(),
    readStoredDirectDocsOpenRequest(),
    bootLocationDocsOpenRequest,
    pendingDocsOpenRequest,
    readStoredPendingDocsOpenRequest(),
    isBrowser() ? window.__pmPendingDocsOpenRequest : undefined,
  ];
  let sawStaleRequest = false;

  for (const request of requestSources) {
    if (!request?.nodeId) {
      continue;
    }
    if (isFreshRequest(request)) {
      return request;
    }
    sawStaleRequest = true;
  }

  if (sawStaleRequest) {
    bootLocationDocsOpenRequest = undefined;
    directLocationDocsOpenRequest = undefined;
    clearWindowDirectDocsOpenRequest();
    pendingDocsOpenRequest = undefined;
    clearStoredDirectDocsOpenRequest();
    clearStoredPendingDocsOpenRequest();
    if (isBrowser()) {
      delete window.__pmPendingDocsOpenRequest;
    }
  }

  return null;
};

export const setPendingDocsOpenRequest = (request: Omit<DocsOpenDocumentRequest, 'requestedAt'>): void => {
  if (!request.nodeId) {
    return;
  }

  pendingDocsOpenRequest = {
    ...request,
    requestedAt: Date.now(),
  };
  writeStoredPendingDocsOpenRequest(pendingDocsOpenRequest);
  writeLocationDocsOpenRequest(pendingDocsOpenRequest);
};

export const clearPendingDocsOpenRequest = (nodeId?: string): boolean => {
  const current = getPendingDocsOpenRequest();
  const activeLocationRequest =
    readLocationDocsOpenRequest() ||
    directLocationDocsOpenRequest ||
    readWindowDirectDocsOpenRequest() ||
    readStoredDirectDocsOpenRequest() ||
    bootLocationDocsOpenRequest;
  const matchesNodeId = nodeIdMatchesRequest(nodeId, current || undefined);
  const hasDifferentLocationTarget = Boolean(
    nodeId &&
    activeLocationRequest?.nodeId &&
    !nodeIdMatchesRequest(nodeId, activeLocationRequest)
  );

  if (!nodeId || matchesNodeId) {
    pendingDocsOpenRequest = undefined;
    clearStoredPendingDocsOpenRequest();
    if (isBrowser()) {
      delete window.__pmPendingDocsOpenRequest;
    }
    if (hasDifferentLocationTarget) {
      return false;
    }
    bootLocationDocsOpenRequest = undefined;
    directLocationDocsOpenRequest = undefined;
    clearWindowDirectDocsOpenRequest();
    clearStoredDirectDocsOpenRequest();
    clearLocationDocsOpenRequest();
    return true;
  }

  return false;
};

export const dispatchDocsOpenDocumentRequest = (
  request: Omit<DocsOpenDocumentRequest, 'requestedAt'>,
  delayMs = 0,
): void => {
  if (!isBrowser() || !request.nodeId) {
    return;
  }

  setPendingDocsOpenRequest(request);
  window.setTimeout(() => {
    window.dispatchEvent(new CustomEvent('docs:open-document', {
      detail: request,
    }));
  }, delayMs);
};

type ProjectNodeRequestRecord = ProjectNode & {
  originalId?: string;
  original_id?: string;
  parent_id?: string | null;
  project_id?: string | null;
};

const asNodeRequestRecord = (node: ProjectNode): ProjectNodeRequestRecord => (
  node as ProjectNodeRequestRecord
);

const nodeProjectId = (node: ProjectNode): string | undefined => {
  const nodeRecord = asNodeRequestRecord(node);
  return node.projectId || nodeRecord.project_id || (isNamespacedId(node.id) ? getProjectIdFromNodeId(node.id) : undefined);
};

const nodeParentId = (node: ProjectNode): string | undefined => {
  const nodeRecord = asNodeRequestRecord(node);
  return node.parentId || nodeRecord.parent_id || undefined;
};

const requestMatchesProject = (
  node: ProjectNode,
  requestedProjectId: string | undefined,
): boolean => {
  if (!requestedProjectId || requestedProjectId === 'default') {
    return true;
  }

  const candidateProjectId = nodeProjectId(node);
  return !candidateProjectId || candidateProjectId === requestedProjectId;
};

const requestStrictlyMatchesProject = (
  node: ProjectNode,
  requestedProjectId: string | undefined,
): boolean => {
  if (!requestedProjectId || requestedProjectId === 'default') {
    return true;
  }

  return nodeProjectId(node) === requestedProjectId;
};

const compactStringList = (values: Array<string | undefined>): string[] => (
  values.filter((value): value is string => typeof value === 'string' && value.length > 0)
);

const requestParentMatchesNode = (
  node: ProjectNode,
  request: Pick<DocsOpenDocumentRequest, 'parentId'>,
): boolean => {
  if (!request.parentId) {
    return true;
  }

  const requestedParentId = getNodeId(request.parentId);
  const currentNodeParentId = nodeParentId(node);
  const normalizedNodeParentId = currentNodeParentId ? getNodeId(currentNodeParentId) : undefined;
  return !normalizedNodeParentId || normalizedNodeParentId === requestedParentId;
};

const requestMetadataMatchesUnscopedNode = (
  node: ProjectNode,
  request: Pick<DocsOpenDocumentRequest, 'title' | 'type' | 'parentId'>,
): boolean => {
  if (request.type && node.type !== request.type) {
    return false;
  }

  if (request.title && node.title !== request.title) {
    return false;
  }

  return requestParentMatchesNode(node, request);
};

const matchesDocsOpenRequestIdentity = (
  node: ProjectNode,
  request: Pick<
    DocsOpenDocumentRequest,
    'nodeId' | 'projectId' | 'candidateNodeIds' | 'title' | 'type' | 'parentId'
  >,
): boolean => {
  const requestedRawNodeId = getNodeId(request.nodeId);
  const requestedProjectId = request.projectId || (isNamespacedId(request.nodeId) ? getProjectIdFromNodeId(request.nodeId) : undefined);
  const nodeRecord = asNodeRequestRecord(node);
  const nodeExactIds = compactStringList([
    node.id,
    nodeRecord.originalId,
    nodeRecord.original_id,
  ]);
  const requestExactIds = compactStringList([
    request.nodeId,
    ...(request.candidateNodeIds || []),
  ]);
  const nodeRawIds = new Set(nodeExactIds.map((value) => getNodeId(value)));
  const requestedRawIds = new Set(
    compactStringList([
      request.nodeId,
      requestedRawNodeId,
      ...(request.candidateNodeIds || []),
    ]).map((value) => getNodeId(value))
  );

  const exactNamespacedIdMatches = requestExactIds.some((requestedId) => (
    isNamespacedId(requestedId) && nodeExactIds.includes(requestedId)
  ));
  if (exactNamespacedIdMatches) {
    return true;
  }

  const rawIdMatches = [...requestedRawIds].some((candidateId) => nodeRawIds.has(candidateId));
  if (rawIdMatches && requestStrictlyMatchesProject(node, requestedProjectId)) {
    return true;
  }
  if (rawIdMatches && !nodeProjectId(node) && requestMetadataMatchesUnscopedNode(node, request)) {
    return true;
  }

  return false;
};

export const matchesDocsOpenRequest = (
  node: ProjectNode,
  request: Pick<
    DocsOpenDocumentRequest,
    'nodeId' | 'projectId' | 'candidateNodeIds' | 'title' | 'type' | 'parentId'
  >,
): boolean => {
  if (matchesDocsOpenRequestIdentity(node, request)) {
    return true;
  }
  const requestedProjectId = request.projectId || (isNamespacedId(request.nodeId) ? getProjectIdFromNodeId(request.nodeId) : undefined);

  if (!request.title || !requestMatchesProject(node, requestedProjectId)) {
    return false;
  }

  if (request.type && node.type !== request.type) {
    return false;
  }

  if (node.title !== request.title) {
    return false;
  }

  return requestParentMatchesNode(node, request);
};

export const findDocsOpenRequestNode = <TNode extends ProjectNode>(
  nodes: TNode[],
  request: Pick<
    DocsOpenDocumentRequest,
    'nodeId' | 'projectId' | 'candidateNodeIds' | 'title' | 'type' | 'parentId'
  > | null | undefined,
): TNode | null => {
  if (!request?.nodeId) {
    return null;
  }
  const matchedById = nodes.find((node) => matchesDocsOpenRequestIdentity(node, request));
  if (matchedById) {
    return matchedById;
  }
  const matchedByIdOrScopedTitle = nodes.find((node) => matchesDocsOpenRequest(node, request));
  if (matchedByIdOrScopedTitle) {
    return matchedByIdOrScopedTitle;
  }

  if (!request.title) {
    return null;
  }

  const uniqueTitleMatches = nodes.filter((node) => (
    node.title === request.title &&
    (!request.type || node.type === request.type)
  ));
  return uniqueTitleMatches.length === 1 ? uniqueTitleMatches[0] : null;
};
